(() => {
  const API_ENDPOINT =
    "https://api-gateway.kalakaarihaath.com/api/base/trade-forms/";

  // Track submission state per form
  const formStates = new WeakMap();

  /**
   * Get full international number (E.164) from intl-tel-input instance.
   * Uses getNumber() when available; otherwise builds from selected country dialCode + input value.
   */
  function getFullPhoneNumber(iti, rawValue) {
    if (!iti) return rawValue ? String(rawValue).trim() : "";
    let full = "";
    try {
      full = iti.getNumber();
    } catch (e) {}
    if (full && typeof full === "string" && full.startsWith("+")) {
      return full.trim();
    }
    const countryData = iti.getSelectedCountryData();
    const dialCode = countryData?.dialCode;
    const national = (rawValue || "")
      .toString()
      .replace(/\s/g, "")
      .replace(/^0+/, "");
    if (dialCode && national) {
      return "+" + dialCode + national;
    }
    return national || (rawValue ? String(rawValue).trim() : "");
  }

  function collectFormData(form) {
    const formDataObj = new FormData(form);
    const termsCheckbox = form.querySelector("#TradeForm-terms");
    const state = formStates.get(form);

    const rawPhone = formDataObj.get("phone");
    const rawPocPhone = formDataObj.get("poc_phone");
    const phone = getFullPhoneNumber(state?.phoneIti, rawPhone);
    const pocPhone = getFullPhoneNumber(state?.pocPhoneIti, rawPocPhone);

    const email = formDataObj.get("email");

    const data = {
      type: formDataObj.get("type"),
      email: email ? email.toLowerCase() : "",
      phone: phone ? phone.trim() : "",
      company: formDataObj.get("company"),
      firm_address: {},
      poc_name: formDataObj.get("poc_name"),
      poc_email: formDataObj.get("poc_email"),
      poc_phone: pocPhone ? pocPhone.trim() : "",
      metadata: {
        firm_address_full: formDataObj.get("firm_address") || "",
        city: formDataObj.get("city"),
        state: formDataObj.get("state"),
        country: formDataObj.get("country"),
        pincode: formDataObj.get("pincode"),
        tandc: termsCheckbox ? termsCheckbox.checked : false,
        social_media_links: formDataObj
          .getAll("social_media_links[]")
          .map((v) => v.trim())
          .filter(Boolean),
        website_link: (formDataObj.get("website_link") || "").trim(),
      },
    };

    return data;
  }

  function showMessage(messageElement, message, type) {
    messageElement.textContent = message;
    messageElement.className = `trade-form__message trade-form__message--${type}`;
    messageElement.setAttribute("role", "alert");
    messageElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  function hideMessage(messageElement) {
    messageElement.className = "trade-form__message hidden";
    messageElement.textContent = "";
  }

  /**
   * Extract error messages from API response when it returns field-keyed arrays,
   * e.g. { poc_phone: ["The phone number entered is not valid."] }
   */
  function getFieldErrorsFromResponse(data) {
    const messages = [];
    if (!data || typeof data !== "object") return messages;

    for (const [key, value] of Object.entries(data)) {
      if (key === "message" || key === "error") continue;
      if (Array.isArray(value)) {
        value.forEach((msg) => {
          if (typeof msg === "string" && msg.trim()) messages.push(msg.trim());
        });
      } else if (typeof value === "string" && value.trim()) {
        messages.push(value.trim());
      }
    }
    return messages;
  }

  function setLoading(submitButton, isLoading) {
    if (!submitButton) {
      return;
    }

    if (isLoading) {
      submitButton.disabled = true;
      submitButton.setAttribute("aria-disabled", "true");
      submitButton.style.pointerEvents = "none";
      submitButton.style.cursor = "not-allowed";
    } else {
      submitButton.disabled = false;
      submitButton.removeAttribute("aria-disabled");
      submitButton.style.pointerEvents = "";
      submitButton.style.cursor = "";
    }
  }

  // field error on validation

  function showFieldError(input, message = "", show = true) {
    const wrapper =
      input.closest("[data-input-wrapper]") || input.closest(".field");
    if (!wrapper) return;

    const messageSpan = wrapper.querySelector("[data-message]");
    if (!messageSpan) return;

    if (show) {
      wrapper.classList.add("has-error");
      messageSpan.classList.remove("hidden");
      messageSpan.textContent = message;
    } else {
      wrapper.classList.remove("has-error");
      messageSpan.classList.add("hidden");
      messageSpan.textContent = "";
    }
  }

  function validateAllFields(form) {
    const inputs = form.querySelectorAll(
      "input[required], select[required], input[type=tel]",
    );
    //  console.log(inputs)
    let isValid = true;
    let firstInvalidField = null;

    inputs.forEach((input) => {
      console.log("valid", validateField(input));
      if (!validateField(input)) {
        isValid = false;
        console.log("valid", isValid, input);
        if (!firstInvalidField) {
          firstInvalidField = input;
        }
      }
    });

    // Social links are mandatory (at least one valid link required)
    const socialResult = validateSocialLinks(form);
    if (!socialResult.isValid) {
      isValid = false;
      if (!firstInvalidField && socialResult.field) {
        firstInvalidField = socialResult.field;
      }
    }

    // Website link is optional; validate only when it has a value
    const websiteInput = form.querySelector("#TradeForm-website-link");
    if (websiteInput && !validateWebsiteLink(websiteInput)) {
      isValid = false;
      if (!firstInvalidField) firstInvalidField = websiteInput;
    }

    if (firstInvalidField) {
      // firstInvalidField.focus();
      firstInvalidField.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    return isValid;
  }

  function validateField(input) {
    const inputType = input.type;
    const inputName = input.name;
    const value = input.value.trim();

    // Email validation
    if (
      inputType === "email" ||
      inputName === "email" ||
      inputName === "poc_email"
    ) {
      if (!validateEmail(value)) {
        showFieldError(input, "Please enter a valid email address");
        return false;
      }
    }

    // phone number validation
    if (inputType === "tel" && value !== "") {
      const isValid = validatePhoneNumber(value);
      if (!isValid) {
        showFieldError(input, "Please enter a valid phone number");
        return false;
      }
    }

    // Pincode validation
    if (inputName === "pincode") {
      if (!validatePincode(value)) {
        showFieldError(input, "Please enter a valid 6-digit pin code");
        return false;
      }
    }

    // Required validation
    if (input.required && value === "") {
      // console.log(value)
      showFieldError(input, "This field is required");
      return false;
    }

    // select box validation
    if (input.tagName === "SELECT" && value === "") {
      showFieldError(input, "Please select an option");
      return false;
    }

    // Field is valid
    showFieldError(input, "", false);
    return true;
  }

  // Social/Website links validation (at least one valid link required)

  function validateSocialLinks(form) {
    const container = form.querySelector("#TradeForm-contact-links");
    if (!container) return { isValid: true, field: null };

    const inputs = Array.from(
      container.querySelectorAll('input[name="social_media_links[]"]'),
    );
    const firstInput = inputs[0] || null;
    const hasValue = inputs.some((input) => input.value.trim() !== "");

    if (!hasValue) {
      if (firstInput) {
        showFieldError(
          firstInput,
          "Please add at least one social link",
        );
      }
      return { isValid: false, field: firstInput };
    }

    let isValid = true;
    let firstInvalid = null;
    inputs.forEach((input) => {
      const value = input.value.trim();
      if (value === "") {
        showFieldError(input, "", false);
        return;
      }
      if (!input.checkValidity()) {
        showFieldError(input, "Please enter a valid URL");
        isValid = false;
        if (!firstInvalid) firstInvalid = input;
      } else {
        showFieldError(input, "", false);
      }
    });

    return { isValid, field: firstInvalid };
  }

  // Optional website link validation (only when a value is present)

  function validateWebsiteLink(input) {
    const value = input.value.trim();
    if (value === "") {
      showFieldError(input, "", false);
      return true;
    }
    if (!input.checkValidity()) {
      showFieldError(input, "Please enter a valid URL");
      return false;
    }
    showFieldError(input, "", false);
    return true;
  }

  // Email validation

  function validateEmail(email) {
    const pattern =
      /^([a-zA-Z0-9_\-\+\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,9})$/;
    return pattern.test(email);
  }

  // phone number validation

  function validatePhoneNumber(phone) {
    const cleaned = phone.replace(/\D/g, "");
    // Check if it's a valid length (7-15 digits is typical for international numbers)
    if (cleaned.length < 7 || cleaned.length > 15) {
      return false;
    }
    // Basic pattern: optional + followed by digits, spaces, hyphens, parentheses
    const pattern =
      /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    return pattern.test(phone);
  }

  // Pincode validation (6 digits)

  function validatePincode(pincode) {
    const pincodeNum = parseInt(pincode, 10);
    return !isNaN(pincodeNum) && pincodeNum >= 100000 && pincodeNum <= 999999;
  }

  function initFieldValidation(form) {
    const inputs = form.querySelectorAll("input[required], select[required]");

    inputs.forEach((input) => {
      // Prevent browser's default validation tooltips
      input.addEventListener("invalid", (e) => {
        e.preventDefault();
        validateField(input);
      });

      // Validate on blur (when user leaves the field)
      input.addEventListener("blur", () => {
        input.dataset.touched = "true";
        validateField(input);
      });

      // Validate on input (real-time as user types)
      input.addEventListener("input", () => {
        // console.log('mvbdj')
        if (input.dataset.touched === "true") {
          validateField(input);
        }
      });

      // Mark as touched on focus
      input.addEventListener("focus", () => {
        input.dataset.touched = "true";
      });
    });

    const websiteLinkInput = form.querySelector("#TradeForm-website-link");
    if (websiteLinkInput) {
      websiteLinkInput.addEventListener("blur", () => {
        websiteLinkInput.dataset.touched = "true";
        validateWebsiteLink(websiteLinkInput);
      });
      websiteLinkInput.addEventListener("input", () => {
        if (websiteLinkInput.dataset.touched === "true") {
          validateWebsiteLink(websiteLinkInput);
        }
      });
      websiteLinkInput.addEventListener("focus", () => {
        websiteLinkInput.dataset.touched = "true";
      });
    }
  }

  async function handleSubmit(
    event,
    form,
    submitButton,
    messageElement,
    state,
  ) {
    event.preventDefault();

    // Prevent multiple submissions
    if (state.isSubmitting) {
      return;
    }

    // Clear previous messages
    hideMessage(messageElement);

    // Validate field and showing error
    if (!validateAllFields(form)) {
      return;
    }

    // Set submitting flag and disable submit button immediately
    state.isSubmitting = true;
    setLoading(submitButton, true);
    // initFieldValidation(form);
    try {
      // Collect form data
      const formData = collectFormData(form);

      // Submit to API
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        showMessage(messageElement, "Form submitted successfully!", "success");
        form.reset();
        submitButton.disabled = true;
      } else {
        // Handle API errors: support field-keyed arrays e.g. { poc_phone: ["..."] }
        let errorMessage =
          data.message ||
          data.error ||
          "Failed to submit form. Please try again.";

        const fieldErrors = getFieldErrorsFromResponse(data);
        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors.join(" ");
        }

        showMessage(messageElement, errorMessage, "error");
      }
    } catch (error) {
      console.error("Trade form submission error:", error);
      showMessage(
        messageElement,
        "An error occurred while submitting the form. Please try again later.",
        "error",
      );
    } finally {
      state.isSubmitting = false;
      setLoading(submitButton, false);
      clearAllErrorField();
    }
  }

  function initTermsModal(form) {
    const trigger = form
      .closest(".trade-form")
      .querySelector(".trade-form__terms-trigger");
    const termsCheckbox = form.querySelector("#TradeForm-terms");
    const modal = form
      .closest(".trade-form")
      .querySelector("#TradeForm-terms-modal");
    const backdrop = modal?.querySelector("[data-close-modal]");
    const acceptBtn = modal?.querySelector("[data-accept-terms]");
    const submitButton = form.querySelector(".trade-form__submit");

    if (!trigger || !modal || !acceptBtn || !termsCheckbox || !submitButton) {
      return;
    }

    function openModal() {
      modal.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
      acceptBtn.focus();
    }

    function closeModal() {
      modal.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      trigger.focus();
    }

    function onAccept() {
      termsCheckbox.checked = true;
      submitButton.disabled = false;
      submitButton.classList.remove("disabled");
      submitButton.removeAttribute("aria-disabled");
      submitButton.style.pointerEvents = "";
      submitButton.style.cursor = "";
      closeModal();
    }

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      openModal();
    });

    termsCheckbox.addEventListener("click", (e) => {
      e.preventDefault();
      openModal();
    });

    acceptBtn.addEventListener("click", onAccept);

    modal.querySelectorAll("[data-close-modal]").forEach((el) => {
      el.addEventListener("click", closeModal);
    });

    modal.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    });
  }

  function initTradeForm(form) {
    // Prevent duplicate event listeners
    if (form.dataset.listenerAttached) {
      return;
    }
    form.dataset.listenerAttached = "true";

    const submitButton = form.querySelector(".trade-form__submit");
    const messageElement = form.querySelector("#TradeForm__message");

    if (!submitButton || !messageElement) {
      console.error("Trade form elements not found");
      return;
    }

    // Initialize state for this form
    const state = { isSubmitting: false };
    formStates.set(form, state);

    // Initialize intl-tel-input (country code selector) on phone fields when library is available
    if (typeof window.intlTelInput === "function") {
      const phoneInput = form.querySelector("#TradeForm-phone");
      const pocPhoneInput = form.querySelector("#TradeForm-poc-phone");
      if (phoneInput) {
        state.phoneIti = window.intlTelInput(phoneInput, {
          initialCountry: "in",
          preferredCountries: ["in", "us", "gb", "ae", "sa"],
          separateDialCode: true,
          countrySearch: false,
          useFullscreenPopup: false,
          utilsScript:
            "https://cdn.jsdelivr.net/npm/intl-tel-input@26.1.1/build/js/utils.js",
        });
      }
      if (pocPhoneInput) {
        state.pocPhoneIti = window.intlTelInput(pocPhoneInput, {
          initialCountry: "in",
          preferredCountries: ["in", "us", "gb", "ae", "sa"],
          separateDialCode: true,
          countrySearch: false,
          useFullscreenPopup: false,
          utilsScript:
            "https://cdn.jsdelivr.net/npm/intl-tel-input@26.1.1/build/js/utils.js",
        });
      }
    }

    initContactLinks(form);
    initTermsModal(form);
    initFieldValidation(form);

    form.addEventListener("submit", (event) => {
      handleSubmit(event, form, submitButton, messageElement, state);
    });

    form.addEventListener("reset", () => {
      submitButton.disabled = true;
      submitButton.classList.add("disabled");
      submitButton.setAttribute("aria-disabled", "true");
      submitButton.style.pointerEvents = "none";
      submitButton.style.cursor = "not-allowed";
      if (state.phoneIti) state.phoneIti.setNumber("");
      if (state.pocPhoneIti) state.pocPhoneIti.setNumber("");
    });
  }

  function initContactLinks(form) {
    const container = form.querySelector("#TradeForm-contact-links");
    const addBtn = form.querySelector("#TradeForm-add-link");
    if (!container || !addBtn) return;

    addBtn.addEventListener("click", () => {
      const item = document.createElement("div");
      item.className = "trade-form__contact-link-item";
      item.innerHTML = `<input type="url" name="social_media_links[]" class="field__input trade-form__contact-link-input" placeholder="https://" autocomplete="off"><button type="button" class="trade-form__contact-link-remove" aria-label="Remove link">&times;</button>`;
      container.appendChild(item);
      item.querySelector("input").focus();
    });

    container.addEventListener("click", (e) => {
      if (!e.target.classList.contains("trade-form__contact-link-remove"))
        return;
      e.target.closest(".trade-form__contact-link-item").remove();
    });
  }

  // clearAllErrorField

  function clearAllErrorField() {
    const tradeFormField = document.querySelectorAll(
      ".trade-form__fields .field",
    );
    console.log("field", tradeFormField);
    tradeFormField.forEach((field) => {
      console.log("field", field);
      field.classList.remove("has-error");
    });
  }

  // Initialize trade form - only once
  function initializeTradeForm() {
    const tradeForm = document.querySelector("#TradeForm");
    if (tradeForm && !tradeForm.dataset.initialized) {
      tradeForm.dataset.initialized = "true";
      initTradeForm(tradeForm);
    }
  }

  // Initialize based on document ready state
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeTradeForm);
  } else {
    initializeTradeForm();
  }
})();
